'use client';

import { type Component, type ComponentProps } from '../../types/component';
import { tv } from '../../utils/tv';
import { Typography, type TypographyProps } from '../typography/Typography';
import { useListContext, useListItemButtonContext } from './ListContext';

const listItemTextClasses = {
  root: 'zds-list-item-text-root',
  primary: 'zds-list-item-text-primary',
  secondary: 'zds-list-item-text-secondary',
} as const;

const listItemTextStyles = tv({
  slots: {
    root: [listItemTextClasses.root, 'flex min-w-0 flex-1 flex-col'],
    primary: [listItemTextClasses.primary],
    secondary: [listItemTextClasses.secondary],
  },
});

interface ListItemTextBaseProps {
  /** Primary text content. */
  primary?: React.ReactNode;
  /** Secondary text content. */
  secondary?: React.ReactNode;
  /** Props forwarded to the primary and secondary `Typography` components. */
  slotProps?: {
    primary?: TypographyProps;
    secondary?: TypographyProps;
  };
}

type ListItemTextProps = ComponentProps<ListItemTextBaseProps, 'span'>;

type ListItemTextComponent = Component<ListItemTextBaseProps, 'span'>;

type TextColor = 'text-primary' | 'text-secondary' | 'static-white' | 'inherit';

interface TextColors {
  primary: TextColor;
  secondary: TextColor;
}

function resolveTextColors(
  color: 'neutral' | 'static-white' | 'accent-primary',
  isDisabled: boolean,
  isSelected: boolean
): TextColors {
  if (isDisabled) {
    return { primary: 'inherit', secondary: 'inherit' };
  }
  if (color === 'static-white') {
    return { primary: 'static-white', secondary: 'static-white' };
  }
  if (color === 'accent-primary' && isSelected) {
    return { primary: 'inherit', secondary: 'inherit' };
  }
  return { primary: 'text-primary', secondary: 'text-secondary' };
}

const ListItemText: ListItemTextComponent = (props) => {
  const { primary, secondary, slotProps, className, ref, ...rest } = props;

  const context = useListContext();
  const buttonContext = useListItemButtonContext();
  const isDisabled = buttonContext?.isDisabled ?? false;
  const isSelected = buttonContext?.isSelected ?? false;
  const color = context?.color ?? 'neutral';

  const styles = listItemTextStyles();
  const { primary: primaryColor, secondary: secondaryColor } =
    resolveTextColors(color, isDisabled, isSelected);

  return (
    <span {...rest} ref={ref} className={styles.root({ className })}>
      {primary != null && (
        <Typography
          component="span"
          variant="body2"
          {...slotProps?.primary}
          className={styles.primary({
            className: slotProps?.primary?.className,
          })}
          color={
            isDisabled ? 'inherit' : (slotProps?.primary?.color ?? primaryColor)
          }
        >
          {primary}
        </Typography>
      )}
      {secondary != null && (
        <Typography
          component="span"
          variant="body2"
          {...slotProps?.secondary}
          className={styles.secondary({
            className: slotProps?.secondary?.className,
          })}
          color={
            isDisabled
              ? 'inherit'
              : (slotProps?.secondary?.color ?? secondaryColor)
          }
        >
          {secondary}
        </Typography>
      )}
    </span>
  );
};

ListItemText.displayName = 'List.ItemText';

export {
  ListItemText,
  type ListItemTextProps,
  type ListItemTextBaseProps,
  listItemTextClasses,
};
